import {
    LINE_COLOR,
    NOTE_COLOR,
    TUNING_HIGHLIGHT_NOTE_COLOR,
    TUNING_HIGHLIGHT_OUTLINE_COLOR,
} from "../tokens";
import {
    getNoteValue,
    HALF_NOTE_SIZE,
    NOTE_CURVE_OFFSET,
    NOTE_SIZE,
    STAVE_SPACING,
} from "./Chant";

export const drawStaveLines = (
    context: CanvasRenderingContext2D,
    xPos: number,
    yPos: number,
    width: number,
    tuningNote: number | undefined
) => {
    context.strokeStyle = LINE_COLOR;
    [1, 2, 3, 4].forEach((offset) => {
        context.beginPath();
        context.moveTo(xPos, yPos + offset * STAVE_SPACING);
        context.lineTo(xPos + width + 0.5, yPos + offset * STAVE_SPACING);
        context.stroke();
    });

    if (tuningNote && tuningNote >= -0.5) {
        const tuningY = yPos + ((9 - tuningNote) / 2) * STAVE_SPACING;
        context.strokeStyle = "rgba(0, 214, 255, 0.5)";
        context.lineWidth = 3;
        context.beginPath();
        context.moveTo(xPos, tuningY);
        context.lineTo(xPos + width + 0.5, tuningY);
        context.stroke();
        context.lineWidth = 1;
    }
};

export const drawFlat = (
    context: CanvasRenderingContext2D,
    noteX: number,
    yPos: number,
    note: string,
    tuningNote: number | undefined
) => {
    const noteValue = getNoteValue(note);

    const isTuningNote = tuningNote === noteValue - 0.5;

    const x = noteX - NOTE_SIZE - 3;
    const y = yPos + ((9 - noteValue) / 2) * STAVE_SPACING;

    const HORIZONTAL_STROKE = 2;

    context.beginPath();
    context.moveTo(x - HALF_NOTE_SIZE, y + 0.7 * HALF_NOTE_SIZE); // Bottom left
    context.lineTo(x + HALF_NOTE_SIZE, y + 1.4 * HALF_NOTE_SIZE); // Bottom right
    context.lineTo(x + HALF_NOTE_SIZE, y - 0.7 * HALF_NOTE_SIZE); // Top right
    context.lineTo(x - HALF_NOTE_SIZE, y - 1.4 * HALF_NOTE_SIZE); // Top left
    context.lineTo(
        x - HALF_NOTE_SIZE,
        y - 1.4 * HALF_NOTE_SIZE + HORIZONTAL_STROKE
    ); // Inner top left
    context.lineTo(
        x + HALF_NOTE_SIZE - 1,
        y - 0.7 * HALF_NOTE_SIZE + HORIZONTAL_STROKE
    ); // Inner top right
    context.lineTo(
        x + HALF_NOTE_SIZE - 1,
        y + 1.4 * HALF_NOTE_SIZE - HORIZONTAL_STROKE
    ); // Inner bottom right
    context.lineTo(
        x - HALF_NOTE_SIZE,
        y + 0.7 * HALF_NOTE_SIZE - HORIZONTAL_STROKE
    ); // Inner bottom left
    context.closePath();
    context.fillStyle = isTuningNote
        ? TUNING_HIGHLIGHT_OUTLINE_COLOR
        : NOTE_COLOR;
    context.fill();

    context.beginPath();
    context.moveTo(x - HALF_NOTE_SIZE, y + 0.7 * HALF_NOTE_SIZE - 0.5);
    context.lineTo(
        x - HALF_NOTE_SIZE,
        y + 0.7 * HALF_NOTE_SIZE - STAVE_SPACING * 1.5
    );
    context.strokeStyle = isTuningNote
        ? TUNING_HIGHLIGHT_OUTLINE_COLOR
        : NOTE_COLOR;
    context.stroke();
};

export const drawNote = (
    context: CanvasRenderingContext2D,
    noteX: number,
    yPos: number,
    note: string,
    specialYOffset: number,
    specialCurveOffset: number | undefined,
    highlighted: boolean,
    options: { shiftFlat?: number },
    tuningNote: number | undefined
) => {
    const noteValue = getNoteValue(note);

    // correct blurriness on non-retina
    noteX += 0.5;
    specialYOffset += 0.5;

    const isFlat = note[note.length - 1] === "b";

    if (isFlat) {
        drawFlat(
            context,
            noteX + (options.shiftFlat ?? 0),
            yPos,
            note,
            tuningNote
        );
    }

    const isTuningNote = isFlat
        ? tuningNote === noteValue - 0.5
        : tuningNote === noteValue;

    context.beginPath();
    context.moveTo(
        noteX - HALF_NOTE_SIZE,
        yPos +
            ((9 - noteValue) / 2) * STAVE_SPACING -
            HALF_NOTE_SIZE +
            specialYOffset
    );
    context.quadraticCurveTo(
        noteX,
        yPos +
            ((9 - noteValue) / 2) * STAVE_SPACING -
            HALF_NOTE_SIZE -
            (specialCurveOffset ?? NOTE_CURVE_OFFSET) +
            specialYOffset,
        noteX + HALF_NOTE_SIZE + 1,
        yPos +
            ((9 - noteValue) / 2) * STAVE_SPACING -
            HALF_NOTE_SIZE +
            specialYOffset
    );
    context.lineTo(
        noteX + HALF_NOTE_SIZE + 1,
        yPos +
            ((9 - noteValue) / 2) * STAVE_SPACING +
            HALF_NOTE_SIZE +
            1 +
            specialYOffset
    );
    context.quadraticCurveTo(
        noteX + 1,
        yPos +
            ((9 - noteValue) / 2) * STAVE_SPACING +
            HALF_NOTE_SIZE +
            1 -
            (specialCurveOffset ?? NOTE_CURVE_OFFSET) +
            specialYOffset,
        noteX - HALF_NOTE_SIZE,
        yPos +
            ((9 - noteValue) / 2) * STAVE_SPACING +
            HALF_NOTE_SIZE +
            1 +
            specialYOffset
    );
    context.closePath();
    context.fillStyle = highlighted
        ? "white"
        : isTuningNote
        ? TUNING_HIGHLIGHT_NOTE_COLOR
        : NOTE_COLOR;
    context.fill();
    if (highlighted) {
        context.strokeStyle = "black";
        context.stroke();
    }
    if (isTuningNote) {
        context.strokeStyle = TUNING_HIGHLIGHT_OUTLINE_COLOR;
        context.stroke();
    }
};

export const drawDiamond = (
    context: CanvasRenderingContext2D,
    noteX: number,
    yPos: number,
    note: string,
    highlighted: boolean,
    tuningNote: number | undefined
) => {
    const noteValue = getNoteValue(note);

    const isTuningNote = tuningNote === noteValue;

    context.fillStyle = highlighted
        ? "white"
        : isTuningNote
        ? TUNING_HIGHLIGHT_NOTE_COLOR
        : NOTE_COLOR;
    context.beginPath();
    context.moveTo(
        noteX + 2,
        yPos + ((9 - noteValue) / 2) * STAVE_SPACING - HALF_NOTE_SIZE - 1
    );
    context.lineTo(
        noteX + HALF_NOTE_SIZE + 2,
        yPos + ((9 - noteValue) / 2) * STAVE_SPACING
    );
    context.lineTo(
        noteX + 2,
        yPos + ((9 - noteValue) / 2) * STAVE_SPACING + HALF_NOTE_SIZE + 1
    );
    context.lineTo(
        noteX - HALF_NOTE_SIZE + 2,
        yPos + ((9 - noteValue) / 2) * STAVE_SPACING
    );
    context.closePath();
    context.fill();
    if (highlighted) {
        context.strokeStyle = "black";
        context.stroke();
    }
    if (isTuningNote) {
        context.strokeStyle = TUNING_HIGHLIGHT_OUTLINE_COLOR;
        context.stroke();
    }
};

export const drawLongDot = (
    context: CanvasRenderingContext2D,
    noteX: number,
    yPos: number,
    note: string
) => {
    const longX = noteX + HALF_NOTE_SIZE + 5;
    const longY =
        yPos +
        ((9 - getNoteValue(note)) / 2) * STAVE_SPACING -
        (getNoteValue(note) % 2 === 0 ? 1 : HALF_NOTE_SIZE + 1);

    context.beginPath();
    context.arc(longX, longY, 1.5, 0, 2 * Math.PI, false);
    context.fillStyle = NOTE_COLOR;
    context.fill();
};
